import { Badge, Button, Code, Group, Paper, ScrollArea, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconAlertCircle, IconChevronDown, IconChevronUp, IconRefresh } from '@tabler/icons-react'
import { ApiError } from '@services/api/apiError'
import { useState } from 'react'

type Props = {
    error: unknown
    onRetry: () => void
    title?: string
    description?: string
    actions?: React.ReactNode
    defaultShowDetails?: boolean 
}

export function QueryErrorFallback({
    error,
    onRetry,
    title = 'Something went wrong',
    description = 'Please try again.',
    actions,
    defaultShowDetails = false,
}: Props) {
    const [showDetails, setShowDetails] = useState(defaultShowDetails)

    const apiErr = error instanceof ApiError ? error : null
    const message = error instanceof Error ? error.message : String(error)

    const originalStack =
        apiErr?.originalError instanceof Error ? apiErr.originalError.stack : undefined

    return (
        <Paper
            radius="md"
            p="md"
            className={'shadow-none!'}
            style={{
                height: showDetails ? '100%' : 'fit-content',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderColor: 'var(--mantine-color-red-5)',
                backgroundColor: 'light-dark(var(--mantine-color-red-1), color-mix(in srgb, var(--mantine-color-red-9), black 70%))',
            }}
        >
            <Group gap="xs" mb="xs">
                <ThemeIcon color="red" variant="subtle" size="sm">
                    <IconAlertCircle size={16} />
                </ThemeIcon>
                <Text fw={600} c="red">
                    {title}
                </Text>
            </Group>

            <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
                <Text size="sm">{apiErr?.message ?? description}</Text>

                <Group justify="flex-start" gap="xs" mt={4}>
                <Button
                    size="xs"
                    variant="light"
                    color="red"
                    leftSection={<IconRefresh size={14} />}
                    onClick={onRetry}
                >
                    Try again
                </Button>

                <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={showDetails ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                    onClick={() => setShowDetails((v) => !v)}
                >
                    {showDetails ? 'Hide details' : 'Show details'}
                </Button>

                {actions}
                </Group>

                {showDetails && (
                    <ScrollArea type="auto" h="100%" style={{ flex: 1, minHeight: 0 }}>
                        <Stack gap="xs" mt={6}>
                            {apiErr ? (
                            <Group gap="xs" wrap="wrap">
                                {typeof apiErr.status === 'number' && <Badge color="red">HTTP {apiErr.status}</Badge>}
                                {apiErr.method && <Badge variant="light" color='red'>{apiErr.method}</Badge>}
                                {apiErr.url && (
                                <Badge
                                    variant="light"
                                    styles={{ label: { maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                    tt={'none'}
                                    color='red'
                                >
                                    {apiErr.url}
                                </Badge>
                                )}
                                {apiErr.requestId && <Badge tt={'none'} variant="light" color='red'>req: {apiErr.requestId}</Badge>}
                            </Group>
                            ) : (
                            <Text size="sm" c="dimmed">
                                {description}
                            </Text>
                            )}
                            <Text size="xs" fw={600}>
                            Message
                            </Text>
                            <Code block>{message}</Code>

                            {apiErr?.debugMessage && apiErr.debugMessage !== apiErr.message ? (
                            <>
                                <Text size="xs" fw={600}>
                                Debug message
                                </Text>
                                <Code block>{apiErr.debugMessage}</Code>
                            </>
                            ) : null}

                            {apiErr ? (
                            <>
                                <Text size="xs" fw={600}>
                                Details
                                </Text>
                                <Code block>
                                {JSON.stringify(
                                    {
                                        status: apiErr.status,
                                        code: apiErr.code,
                                        method: apiErr.method,
                                        url: apiErr.url,
                                        requestId: apiErr.requestId,
                                        isNetworkError: apiErr.isNetworkError,
                                        isTimeout: apiErr.isTimeout,
                                        isCanceled: apiErr.isCanceled,
                                        data: apiErr.data,
                                    },
                                    null,
                                    2,
                                )}
                                </Code>

                                {apiErr.stack ? (
                                <>
                                    <Text size="xs" fw={600}>
                                    Stack
                                    </Text>
                                    <Code block>{apiErr.stack}</Code>
                                </>
                                ) : null}

                                {originalStack ? (
                                <>
                                    <Text size="xs" fw={600}>
                                    Original stack
                                    </Text>
                                    <Code block>{originalStack}</Code>
                                </>
                                ) : null}
                            </>
                            ) : null}
                        </Stack>
                    </ScrollArea>
                )}
            </Stack>
        </Paper>
    )
}
